import { jwtDecode } from "jwt-decode";
import axiosInstance from "../routes/axiosConfig";

const TOKEN_KEY = "jwtToken";

const authService = {
  login: async (credentials) => {
    try {
      const requestBody = {
        login: credentials.email || credentials.login,
        password: credentials.password,
      };

      authService.logout();

      const response = await axiosInstance.post(
        "/employees/login",
        requestBody,
        { headers: { Authorization: undefined } }
      );

      const rawToken = response.data;
      const token =
        typeof rawToken === "string"
          ? rawToken.trim()
          : String(rawToken ?? "").trim();

      if (!token) {
        throw new Error("Token de autenticação não retornado pelo servidor.");
      }

      localStorage.setItem(TOKEN_KEY, token);

      const decodedToken = jwtDecode(token);

      if (!decodedToken?.sub) {
        throw new Error("Token inválido: usuário não identificado.");
      }

      return {
        token: token,
        userId: decodedToken.sub,
        login: decodedToken.sub,
        name: decodedToken.name,
        roles: decodedToken.roles,
      };
    } catch (error) {
      console.error("Erro no login:", error);
      const errorMessage =
        error.response?.data?.message ||
        "Falha no login. Verifique suas credenciais.";
      throw new Error(errorMessage);
    }
  },

  logout: () => {
    localStorage.removeItem(TOKEN_KEY);
  },

  getToken: () => {
    const token = localStorage.getItem(TOKEN_KEY);
    return token ? token.trim() : null;
  },

  getCurrentUser: () => {
    const token = authService.getToken();
    if (token) {
      try {
        const decodedToken = jwtDecode(token);
        if (decodedToken.exp * 1000 > Date.now()) {
          return {
            userId: decodedToken.sub,
            login: decodedToken.sub,
            name: decodedToken.name,
            roles: decodedToken.roles,
          };
        } else {
          console.warn("Token JWT expirado. Realizando logout.");
          authService.logout();
          return null;
        }
      } catch (error) {
        console.error("Erro ao decodificar token JWT:", error);
        authService.logout();
        return null;
      }
    }
    return null;
  },

  hasRole: (requiredRole) => {
    const user = authService.getCurrentUser();

    if (!user || !user.roles) {
      return false;
    }

    const userRoles = Array.isArray(user.roles) ? user.roles : [user.roles];
    const userRolesUpperCase = userRoles.map((role) =>
      String(role).toUpperCase()
    );
    const normalizedRequired = requiredRole.toUpperCase();
    const roleName = normalizedRequired.startsWith("ROLE_")
      ? normalizedRequired
      : `ROLE_${normalizedRequired}`;

    return (
      userRolesUpperCase.includes(roleName) ||
      userRolesUpperCase.includes(normalizedRequired)
    );
  },

  hasAnyRole: (requiredRoles) => {
    const user = authService.getCurrentUser();

    if (!user || !user.roles) {
      return false;
    }

    const userRoles = Array.isArray(user.roles) ? user.roles : [user.roles];
    const userRolesUpperCase = userRoles.map((role) =>
      String(role).toUpperCase()
    );

    return requiredRoles.some((role) => {
      const normalizedRequired = String(role).toUpperCase();
      const roleName = normalizedRequired.startsWith("ROLE_")
        ? normalizedRequired
        : `ROLE_${normalizedRequired}`;

      return (
        userRolesUpperCase.includes(roleName) ||
        userRolesUpperCase.includes(normalizedRequired)
      );
    });
  },
};

export default authService;
